import { PRODUCT_STATUS_VARIANT_MAP } from '#interfaces/productStatus'
import { SUBSCRIPTION_STATUS_LABEL_KEYS, SUBSCRIPTION_STATUS_VARIANTS } from '#interfaces/subscription'
import type { TStatusGroup } from '#lib/statusGroups'

/**
 * The tabs the overview offers (DEV-2501). MAG-123 puts `initial` first:
 * those subscriptions were hidden, and a super admin could neither see them
 * nor withdraw them.
 *
 * The requested tab covers `ready_for_request` and `requested_at_provider`
 * (DEV-2503 sets the latter when the per-carrier export runs). The active tab
 * is `active`: bulk activation moves a confirmed subscription straight there.
 */
export type TSubscriptionTab = 'initial' | 'requested' | 'active' | 'ending' | 'ended'

/**
 * Every tab names its statuses explicitly, so each status lands on exactly one
 * of them. `initial` has its own tab (MAG-123) rather than being left
 * unreachable: the subscription is waiting on a card order, and the only
 * action is to withdraw it in bulk.
 *
 * The terminal tab is the exception the parent ticket did not foresee. It said
 * the other statuses stay hidden, but that predates DEV-2502 and DEV-2507
 * giving a super admin a cancel and an end button here — and a subscription
 * that vanishes the moment you act on it gives no confirmation that anything
 * happened.
 */
export const SUBSCRIPTION_TAB_STATUSES: Record<TSubscriptionTab, readonly string[]> = {
  // Waiting on the card order. Nothing has gone to a carrier, so the row can
  // be withdrawn in bulk and nothing else (MAG-123). Visible order is the
  // tabs memo in index.tsx; this key is first only so the map reads that way.
  initial: ['initial'],
  // Requested by an admin, and at the carrier once the export has run.
  // `requested_at_provider` is what the per-carrier export sets (DEV-2503).
  requested: ['ready_for_request', 'requested_at_provider'],
  // Bulk activation moves a confirmed subscription to active.
  active: ['active'],
  // Ending is the single status an admin termination reaches. A super admin
  // still finalises it to ended.
  ending: ['ending'],
  // The terminal states share one tab. They are not the same thing — cancelled
  // never started, ended ran and was closed by hand, expired simply reached its
  // last day (DEV-2510) — but the status badge on each row says which, so
  // splitting them would buy tabs and no information. Without this, a
  // subscription would drop out of the overview the night its term ran out.
  ended: ['ended', 'cancelled', 'expired'],
}

/**
 * `?tab=cancelled` is what the tab was called while it held that status alone.
 * Kept resolving so links written down before DEV-2507 still open the tab their
 * author meant rather than silently falling back to the first one.
 */
const LEGACY_TAB_ALIASES: Record<string, TSubscriptionTab> = {
  cancelled: 'ended',
}

/**
 * Which subscriptions the cancel action applies to, mirroring the backend's
 * `SubscriptionStatus::canBeCancelled()`.
 *
 * Two different acts share one button. Before the export it is a withdrawal of
 * something no carrier has seen (DEV-2502). After it, `requested_at_provider`
 * is the other half of activating: the carrier confirmed it and a super admin
 * decides not to hand it to the cardholder.
 */
export const CANCELLABLE_STATUSES: readonly string[] = ['initial', 'ready_for_request', 'requested_at_provider']

export const isCancellable = (subscriptionStatus: string): boolean => CANCELLABLE_STATUSES.includes(subscriptionStatus)

/**
 * The status group per lifecycle status, shared by the subscription and the
 * product column: the one subscription map and the one product map, so this
 * overview colours a status as every other screen does (src/lib/statusGroups.ts).
 * The subscription map is exhaustive, so a status the contract adds fails the
 * build; anything in neither vocabulary still falls through to grey.
 */
const STATUS_VARIANTS: Record<string, TStatusGroup> = {
  ...PRODUCT_STATUS_VARIANT_MAP,
  ...SUBSCRIPTION_STATUS_VARIANTS,
}

export const statusBadgeVariant = (status: string): TStatusGroup => STATUS_VARIANTS[status] ?? 'neutral'

// The same keys getProductStatusLabel uses, so the card column matches the
// rest of the application. `active` and `ended` are already on the
// subscription map.
const PRODUCT_STATUS_LABEL_KEYS: Record<string, string> = {
  ordering: 'Being ordered',
  ordered: 'Ordered',
  in_production: 'In production',
  shipped: 'Shipped',
  photo_rejected: 'Photo rejected',
  blocking: 'Being blocked',
  blocked: 'Blocked',
  lost: 'Lost',
  stolen: 'Stolen',
}

/**
 * Subscription labels come from the one map every surface uses. The product
 * half stays beside it. An unknown status still falls back to the raw code.
 */
const STATUS_LABEL_KEYS: Record<string, string> = {
  ...SUBSCRIPTION_STATUS_LABEL_KEYS,
  ...PRODUCT_STATUS_LABEL_KEYS,
}

export const statusLabelKey = (status: string): string => STATUS_LABEL_KEYS[status] ?? status

/**
 * Which subscriptions the activate action applies to, mirroring the backend's
 * SubscriptionStatus::canBeActivated() and the card check beside it (DEV-2504).
 *
 * Activating bills the company and writes to the cardholder, so it is offered
 * only where it can actually succeed: the carrier has confirmed, and the card
 * works.
 */
export const isActivatable = (subscriptionStatus: string, productStatus: string): boolean =>
  subscriptionStatus === 'requested_at_provider' && productStatus === 'active'

/** The tab the overview opens on when the URL does not name one. */
export const DEFAULT_SUBSCRIPTION_TAB: TSubscriptionTab = 'requested'

export function isSubscriptionTab(value: unknown): value is TSubscriptionTab {
  return typeof value === 'string' && value in SUBSCRIPTION_TAB_STATUSES
}

/** The tab a URL names, its retired name, or the default when it names neither. */
export function resolveSubscriptionTab(value: unknown): TSubscriptionTab {
  if (isSubscriptionTab(value)) return value
  if (typeof value !== 'string') return DEFAULT_SUBSCRIPTION_TAB

  return LEGACY_TAB_ALIASES[value] ?? DEFAULT_SUBSCRIPTION_TAB
}

export interface ISubscriptionFilters {
  companyId: string
  subscriptionType: string
}

export const EMPTY_SUBSCRIPTION_FILTERS: ISubscriptionFilters = {
  companyId: '',
  subscriptionType: '',
}

export interface ISubscriptionListItem {
  id: string
  subscriptionType: string
  subscriptionTypeLabel: string
  subscriptionStatus: string
  subscriptionStatusLabel: string
  productStatus: string
  productStatusLabel: string
  cardNumber: string | null
  companyId: string | null
  companyName: string | null
  person: string | null
  email: string | null
  startDate: string | null
  endDate: string | null
  chargedAmountExVatCents: number | null
  renewSubscription: boolean
  renewedFromId: string | null
  createdAt: string
}
